import { LockOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Col, Form, Input, Layout, Menu, Row, Select, Space, Typography } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import { omit } from "lodash";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const accountUserQuery = gql`
  query(
    $pagination: ConnectionPagination
    $priceListSorting: ValuePriceListSorting
    $paymentMethodSorting: AccountPaymentMethodSorting
    $searchQuery: String
  ) {
    valuePriceLists(pagination: $pagination, sorting: $priceListSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    accountPaymentMethods(pagination: $pagination, sorting: $paymentMethodSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameIt
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const createAccountUserMutation = gql`
  mutation($input: CreateAccountUserInput!) {
    createAccountUser(input: $input) {
      user {
        id
        code
        email
        role
        language
        priceList {
          id
          code
        }
        paymentMethod {
          id
          code
        }
        companyName
        address
        zip
        city
        province
        country
        vatNumber
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const { error, loading: queryLoading, data } = useQuery(accountUserQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(createAccountUserMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate(`../${data.createAccountUser.user.id}`);
    },
  });

  const onFinish = (values: any) => {
    const cleanValues = omit(values, ["passwordConfirmation"]);
    execMutation({ variables: { input: cleanValues } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const {
    valuePriceLists: { nodes: valuePriceLists },
    accountPaymentMethods: { nodes: accountPaymentMethods },
  } = data;

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Typography.Title level={4} style={{ lineHeight: "auto" }}>
                New User
              </Typography.Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border no-margin">
              <NavLink to="../">
                <Button>Cancel</Button>
              </NavLink>
            </Menu.Item>
            <Menu.Item className="no-border">
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}

            <Form
              layout="vertical"
              form={form}
              initialValues={{ password: "", isAdmin: false, authorization: { canManageAccounts: false } }}
              onFinish={onFinish}
            >
              <Row gutter={[24, 0]}>
                <Col span={8}>
                  <Form.Item name="code" label="Code" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="email" label="Email" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="role" label="Role" rules={[{ required: true, message: "is required" }]}>
                    <Select>
                      <Select.Option value="USER">User</Select.Option>
                      <Select.Option value="SALESPERSON">Salesperson</Select.Option>
                      <Select.Option value="ADMIN">Admin</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="language" label="Language" rules={[{ required: true, message: "is required" }]}>
                    <Select>
                      <Select.Option value="EN">English</Select.Option>
                      <Select.Option value="IT">Italiano</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="priceListId" label="Price List" rules={[{ required: true, message: "is required" }]}>
                    <Select>
                      {valuePriceLists.map((priceList: any) => (
                        <Select.Option key={priceList.id} value={priceList.id}>
                          {priceList.code}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="paymentMethodId"
                    label="Payment Method"
                    rules={[{ required: true, message: "is required" }]}
                  >
                    <Select>
                      {accountPaymentMethods.map((paymentMethod: any) => (
                        <Select.Option key={paymentMethod.id} value={paymentMethod.id}>
                          {paymentMethod.code} - {paymentMethod.nameIt}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true, message: "is required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="address" label="Address">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="zip" label="Zip">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="city" label="City">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="province" label="Province">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="country" label="Country">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="vatNumber" label="Vat Number">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="password" label="Password" rules={[{ required: true, message: "is required" }]}>
                    <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="passwordConfirmation"
                    label="Confirm Password"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "is required" },
                      ({ getFieldValue }) => ({
                        validator(rule, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject("not matching");
                        },
                      }),
                    ]}
                  >
                    <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
